import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InfoHintComponent } from '../../../components/shared/info-hint.component';
import { RangeSwitchComponent } from '../../../components/shared/range-switch.component';
import { DailyBar, DailyBarsComponent } from '../../../components/shared/daily-bars.component';
import { PersonLinkComponent } from '../../../components/shared/person-link.component';
import { FeedPostListComponent } from '../../../components/shared/feed-post-list.component';
import { UsersService } from '../../../services/users-service.service';
import {
  EventInsightDetailDto,
  EventInsightMemberDto,
} from '../../../data-transfer-objects/events-insight.dto';
import { LOBBY_STATE_HINT, LOBBY_STATE_LABEL } from '../events.component';
import { EventInfoDialogComponent } from '../event-info-dialog/event-info-dialog.component';
import {
  PeerChatsDialogComponent,
  PeerChatsDialogData,
} from '../peer-chats-dialog/peer-chats-dialog.component';

type MemberSort = 'talkative' | 'joined' | 'seen';

/**
 * One space, in full.
 *
 * What the board's row summarises, laid out so the organizer's situation is
 * readable: what the space is, who runs it, who joined and when, who is still
 * waiting at the door, and how the lobby has actually been used - day by day,
 * and person by person. Click any name for the same profile dialog the users
 * list opens.
 */
@Component({
  selector: 'app-event-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    InfoHintComponent,
    RangeSwitchComponent,
    DailyBarsComponent,
    PersonLinkComponent,
    FeedPostListComponent,
  ],
  templateUrl: './event-detail.component.html',
  styleUrls: ['./event-detail.component.scss'],
})
export class EventDetailComponent implements OnInit {
  id = '';
  event: EventInsightDetailDto | null = null;
  loading = true;
  error: string | null = null;
  days = 28;
  memberSort: MemberSort = 'talkative';

  readonly stateLabel = LOBBY_STATE_LABEL;
  readonly stateHint = LOBBY_STATE_HINT;

  constructor(
    private route: ActivatedRoute,
    private usersService: UsersService,
    private dialog: MatDialog
  ) {}

  /**
   * Everything this space is set to, on one page.
   *
   * Opened from the already-loaded event rather than re-fetching: the detail
   * call returns every column, so a second round trip would only add a spinner
   * between the click and the answer.
   */
  openPreferences(): void {
    if (!this.event) return;
    this.dialog
      .open(EventInfoDialogComponent, {
        width: '100vw',
        maxWidth: '100vw',
        data: this.event,
        panelClass: 'event-info-panel',
      })
      .afterClosed()
      .subscribe((result) => {
        // Reload rather than trust the form: a save re-pins the map and may
        // change what the page shows beyond the fields that were edited.
        if (result?.saved) this.load();
      });
  }

  /**
   * Who chatted with whom, from the tile that counts them.
   *
   * The detail is already loaded, so it is handed to the dialog rather than
   * fetched again - the board opens the same dialog without one and pays for
   * the round trip there.
   */
  openPeerChats(): void {
    if (!this.event) return;
    this.dialog.open<PeerChatsDialogComponent, PeerChatsDialogData>(PeerChatsDialogComponent, {
      width: '760px',
      maxWidth: '96vw',
      autoFocus: false,
      data: { eventId: this.event.id, title: this.event.title, detail: this.event },
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.id = params.get('id') ?? '';
      this.load();
    });
  }

  load(): void {
    if (!this.id) return;
    this.loading = true;
    this.error = null;
    this.usersService.getEventInsight(this.id, this.days).subscribe({
      next: (e) => {
        this.event = e;
        this.describePosts(e);
        this.loading = false;
      },
      error: (err) => {
        this.error =
          err?.status === 404
            ? 'No space with this id.'
            : `Could not load the space: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  setRange(days: number): void {
    this.days = days;
    this.load();
  }

  get members(): EventInsightMemberDto[] {
    const list = [...(this.event?.members ?? [])];
    const by: Record<MemberSort, (a: EventInsightMemberDto, b: EventInsightMemberDto) => number> = {
      talkative: (a, b) =>
        b.lobby.messages - a.lobby.messages || (b.lobby.lastAt ?? '').localeCompare(a.lobby.lastAt ?? ''),
      joined: (a, b) => b.joinedAt.localeCompare(a.joinedAt),
      seen: (a, b) => (b.lastSeen ?? '').localeCompare(a.lastSeen ?? ''),
    };
    return list.sort(by[this.memberSort]);
  }

  get pending() {
    return (this.event?.requests ?? []).filter((r) => r.status === 'Pending');
  }

  get rejected() {
    return (this.event?.requests ?? []).filter((r) => r.status !== 'Pending');
  }

  /** Members who have never posted in the lobby. */
  get silentMembers(): number {
    return (this.event?.members ?? []).filter((m) => m.lobby.messages === 0).length;
  }

  /**
   * Feed posts this space is credited with that no member can actually open.
   *
   * The count comes off the posts table; the feed hides posts whose author is
   * banned or gone. A space could therefore read "2 feed posts" with nothing
   * behind it for anyone but an admin, which is the kind of gap that gets
   * read as a broken number rather than as a blocked account.
   */
  get hiddenPosts(): number {
    return this.hiddenPostIds.length;
  }

  /*
   * Worked out once per load rather than read off a getter: both are passed
   * straight into an @Input, and a getter that builds a fresh array or object
   * every change-detection pass hands the child a new reference each time.
   */
  hiddenPostIds: string[] = [];
  postNotes: Record<string, string> = {};

  /** The line under a post: why it is dimmed, or that a stranger wrote it. */
  private describePosts(event: EventInsightDetailDto): void {
    const posts = event.feedPosts ?? [];
    this.hiddenPostIds = posts.filter((p) => p.hiddenFromFeed).map((p) => p.id);
    this.postNotes = {};
    for (const p of posts) {
      if (p.hiddenFromFeed) {
        this.postNotes[p.id] = p.author
          ? 'Counted here, but hidden from the feed while the author is blocked.'
          : 'Counted here, but hidden from the feed — the author’s account is gone.';
      } else if (!p.byOrganizer) {
        this.postNotes[p.id] = 'Posted by someone who does not run this space.';
      }
    }
  }

  organizerShare(): number | null {
    const l = this.event?.lobby;
    if (!l) return null;
    const s = l.window.messages ? l.window : l.allTime;
    return s.messages ? Math.round((s.organizerMessages / s.messages) * 100) : null;
  }

  percent(rate: number | null): string {
    return rate === null ? '—' : `${Math.round(rate * 100)}%`;
  }

  get dailyBars(): DailyBar[] {
    return (this.event?.daily ?? []).map((d) => ({
      day: d.day,
      value: d.messages,
      title: `${d.day}: ${d.messages} messages from ${d.voices} people`,
    }));
  }

  get windowVoices(): number {
    return this.event?.lobby.window.voices ?? 0;
  }

  /** What the organizer should take from the state, in one line. */
  get advice(): string {
    const e = this.event;
    if (!e) return '';
    const share = this.organizerShare();
    switch (e.lobby.state) {
      case 'silent':
        return e.memberCount
          ? `${e.memberCount} people joined and nobody has posted. The room needs a first message from the organizer.`
          : 'Nobody has joined, so there is nobody to talk. Start with membership.';
      case 'quiet':
        return `The lobby had ${e.lobby.allTime.messages} messages before but none in the last ${e.range.days} days.`;
      case 'one-voice':
        return share !== null && share >= 80
          ? 'Only the organizer posts. Members read but do not answer — try asking them something directly.'
          : 'One member is carrying the room alone in this range.';
      case 'some-replies':
        return 'People answer, but threads die quickly. Prompts that invite a second reply would help.';
      case 'conversation':
        return 'The room works: members talk to each other. Watch that it stays this way after the event.';
    }
  }

  ago(iso: string | null): string {
    if (!iso) return '—';
    const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  get location(): string | null {
    const e = this.event;
    if (!e) return null;
    return (
      [e.venueName, e.eventLocation?.name, e.eventLocation?.address, e.locationDescription]
        .filter((v, i, all) => v && all.indexOf(v) === i)
        .join(' · ') || null
    );
  }
}
